import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface AuthResponse {
  token: string;
}

const MagicLinkHandler: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setError('No token found in URL.');
      return;
    }
    
    const handleAuth = async () => {
      try {
        
        // Add access granted header for password protection
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        
        // Add access granted header if password protection was bypassed
        const accessGranted = localStorage.getItem('thinkmate_access_granted');
        if (accessGranted === 'true') {
          headers['x-access-granted'] = 'true';
        } else {
          // Include access code for password protection
          headers['x-access-code'] = 'zero2one';
        }
        
        // Verify magic link
        const authResponse = await axios.post<AuthResponse>(`${API_URL}/api/auth/verify-magic-link`, { token }, { headers });
        const jwtToken = authResponse.data.token;
        localStorage.setItem('token', jwtToken);
        
        // Grant access automatically since magic link authentication is trusted
        localStorage.setItem('thinkmate_access_granted', 'true');
        localStorage.setItem('thinkmate_access_granted_at', Date.now().toString());
        
        // Fetch user profile with the new token
        try {
          const profileHeaders = {
            ...headers,
            'x-auth-token': jwtToken
          };
          const profileResponse = await axios.get(`${API_URL}/api/user/profile`, { headers: profileHeaders });
          
          // Store user profile
          const userProfile = {
            id: profileResponse.data._id,
            email: profileResponse.data.email,
            name: profileResponse.data.name,
            onboardingStatus: profileResponse.data.onboardingStatus || 'in_progress'
          };
          localStorage.setItem('userProfile', JSON.stringify(userProfile));
        } catch (profileErr) {
          console.warn('Failed to fetch user profile, but auth succeeded:', profileErr);
        }
        
        // Force a reload so all hooks/components see the new token and profile
        window.location.replace('/');
        return;
      } catch (err) {
        console.error('Magic link verification failed:', err);
        setStatus('error');
        setError('Invalid or expired magic link.');
      }
    };

    handleAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
        {status === 'loading' && <p>Verifying magic link...</p>}
        {status === 'success' && <p className="text-green-600">Login successful! Redirecting...</p>}
        {status === 'error' && <p className="text-red-600">{error}</p>}
      </div>
    </div>
  );
};

export default MagicLinkHandler; 